import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { BarChart, BarPlot } from '@mui/x-charts/BarChart';
import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { LinePlot, MarkPlot } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
    AlertTriangle,
    ArrowLeftRight,
    BarChart3,
    Banknote,
    Calendar,
    CheckCircle2,
    Inbox,
    RefreshCw,
    Table2,
    TrendingDown,
    TrendingUp,
    Wallet,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CASH_FLOW_INVALIDATE_EVENT } from '../../common/cashFlowEvents';
import { httpClient } from '../../common/httpclient';
import { FilterChips } from '../../component/filter/FilterToolbar';
import ReportFilterBar from '../../component/report/ReportFilterBar';
import DialogForm from '../../component/dialog/DialogForm';
import AnimatedNumber from '../../component/common/AnimatedNumber';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { usePermissions } from '../../hooks/usePermissions';
import '../reports/reports.css';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { protoIcon } from '../../component/common/ProtoIcon';
import MobileReport, { MobileReportBody, formatMoneyKpi, useMobileRegenerate } from '../../component/report/MobileReport';
import { AreaLine, BarsChart, Caption, ChartCard, DonutBlock, SeriesLegend } from '../../component/report/MobileSalesSections';
import { CashFlowNotices, MonthlyCashList } from '../../component/report/MobileCashFlowSections';
import { KitSeg } from '../../component/common/MobileKitExtras';
import { KitEmptyNote } from '../../component/common/MobileDashboardKit';

/**
 * Cobertura de dados x protótipo (`prototipo-relatorio-fluxo-caixa.html`):
 *
 * `GET /dashboard/cash-flow` (`dashboard/cash_flow.go`) já devolve exatamente
 * o que a tabela mensal e os 6 KPIs do protótipo precisam — nenhum endpoint
 * novo foi criado nesta tarefa: `opening_balance`/`total_inflows`/
 * `total_outflows`/`net_period`/`closing_balance` (agregados do período) e um
 * array `period` com uma linha por mês-calendário (`inflows`/`outflows`/
 * `net`/`opening_balance`/`closing_balance`). Entradas = receitas por data de
 * lançamento; saídas = despesas com status "Prestado Contas" pela data de
 * pagamento (ou lançamento, se não paga) — mesma regra de negócio da versão
 * anterior desta tela, preservada 1:1 (só a apresentação mudou).
 *
 * Lacunas reais entre o protótipo e o que a API expõe (documentadas, não
 * inventadas):
 *  1. Os chips de variação (↑11.2%/↑9.1%/↑14.5%) nos KPIs de Entradas/
 *     Saídas/Líquido do protótipo comparam com um período anterior — o
 *     endpoint não devolve nenhum dado de período anterior (diferente de
 *     `/chart/sales`, que tem `*_growth_percentage`). Os cards de KPI aqui
 *     mostram só o valor real, sem chip de delta fabricado.
 *  2. O campo "Agrupar por" (Mês/Trimestre/Ano) do toolbar do protótipo foi
 *     omitido: o endpoint não tem parâmetro de agrupamento (sempre devolve
 *     uma linha por mês) e o próprio item do checklist descreve esta tela
 *     como "tabela mensal". O toolbar real só tem Início/Fim, que já eram
 *     funcionais na versão anterior desta tela.
 *  3. O donut "Saídas por categoria" do protótipo não tem dado equivalente —
 *     `category_transaction` só distingue `receita`/`despesa`, não existe
 *     subcategoria de despesa (fornecedores/folha/impostos/etc.) em nenhuma
 *     tabela. Substituído por um donut real "Composição do período"
 *     (participação de entradas x saídas no total movimentado), a mesma
 *     ideia de "substituir por um gráfico real quando o dado do protótipo
 *     não existe" já usada em `VehicleReport.js`.
 *  4. "Entradas x Saídas por trimestre" e "Meses positivos x negativos" são
 *     calculados no cliente agregando as linhas mensais que a própria API já
 *     devolve (soma real, sem endpoint novo nem número fabricado) — os
 *     trimestres aqui são trimestres-calendário rotulados "T1/2026" etc, não
 *     o rótulo fiscal cruzando dois anos do protótipo ("Q1 25/26").
 *  5. Combo chart (barras de entradas/saídas divergindo de uma linha de base
 *     + linha de saldo acumulado em eixo Y independente sobreposta) é um
 *     padrão de UI novo, implementado com `@mui/x-charts` `ChartContainer` +
 *     `BarPlot`/`LinePlot` (stacking `diverging` nativo da lib para as
 *     barras positivas/negativas) — nenhuma biblioteca de gráficos nova foi
 *     introduzida. Documentado como extensão em `DESIGN-SYSTEM.md` §5.20.
 */

const CASH_FLOW_PERMISSIONS = ['report.cashflow', 'dashboard.sale'];

const formatBrl = (v) =>
    Number(v ?? 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

// Mesmo texto de `formatBrl`, com animação de contagem (KPIs).
const animBrl = (v) => <AnimatedNumber value={Number(v ?? 0)} decimals={2} format={formatBrl} />;

/** @param {import('dayjs').Dayjs} d */
const toApiDate = (d) => (d && d.isValid() ? d.format('DD/MM/YYYY') : '');

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const monthAxisLabel = (row) => capitalize(`${row.month_label}/${row.year}`);


// KPI card — DESIGN-SYSTEM.md §5.2 "KPI card": ícone chip 34px tintado no
// topo, valor grande neutro (ou semântico via `valueColor`), legenda pequena.
function KpiCard({ icon, iconBg, iconFg, value, valueColor, label, loading }) {
    return (
        <Card>
            <CardContent sx={{ p: { xs: '10px', md: 2 }, '&:last-child': { pb: { xs: '10px', md: 2 } } }}>
                <Box sx={{ width: { xs: 28, md: 34 }, height: { xs: 28, md: 34 }, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: iconBg, color: iconFg, mb: { xs: '6px', md: 1.5 } }}>
                    {icon}
                </Box>
                {loading ? (
                    <Skeleton variant="text" width={90} height={30} sx={{ mb: 0.25 }} />
                ) : (
                    <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: 800, letterSpacing: '-0.02em', color: valueColor || 'text.primary', mb: 0.25, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
                        {value}
                    </Typography>
                )}
                <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
            </CardContent>
        </Card>
    );
}

// `.pill` — dot + rótulo, usado para líquido positivo/negativo por mês.
const PILL_TONE_SX = {
    green: { bgcolor: 'success.bg', color: 'success.main' },
    red: { bgcolor: 'error.bg', color: 'error.main' },
};
function Pill({ label, tone }) {
    const toneSx = PILL_TONE_SX[tone] || PILL_TONE_SX.green;
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px', ...toneSx }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
            {label}
        </Box>
    );
}

// DESIGN-SYSTEM.md §5.11 empty state (variante compacta).
function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

// `.legend-item` — dot + rótulo + valor em negrito, usado sob os donuts/gráficos.
function LegendRow({ color, label, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '11.5px', color: 'text.secondary', width: '100%' }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
            <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
            <Typography component="span" sx={{ fontSize: '11.5px', fontWeight: 700, color: 'text.primary', ml: 'auto' }}>{value}</Typography>
        </Box>
    );
}

// Painel de gráfico/card genérico (`.panel-head`).
function ChartPanel({ title, subtitle, loading, children }) {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2 }}>
                    {loading ? (
                        <>
                            <Skeleton variant="text" width={160} height={20} />
                            <Skeleton variant="text" width={200} height={14} />
                        </>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
                            {subtitle ? <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>{subtitle}</Typography> : null}
                        </>
                    )}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
            </CardContent>
        </Card>
    );
}

// Donut (PieChart real via @mui/x-charts) com overlay central + legenda.
function DonutPanel({ title, subtitle, data, centerValue, centerCaption, loading, emptyTitle, emptySubtitle }) {
    const hasData = data.some((item) => item.value > 0);
    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <Skeleton variant="circular" width={128} height={128} />
                    <Skeleton variant="text" width="80%" height={16} />
                    <Skeleton variant="text" width="70%" height={16} />
                </Box>
            ) : hasData ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ position: 'relative', width: 128, height: 128 }}>
                        <PieChart
                            series={[{ data, innerRadius: 38, outerRadius: 64, paddingAngle: 2, cornerRadius: 3 }]}
                            height={128}
                            width={128}
                            slotProps={{ legend: { hidden: true } }}
                            tooltip={{ trigger: 'item' }}
                            sx={{ '& .MuiChartsLegend-root': { display: 'none' } }}
                        />
                        <Box sx={{ position: 'absolute', inset: 18, borderRadius: '50%', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: 'text.primary', lineHeight: 1.1 }}>{centerValue}</Typography>
                            <Typography sx={{ fontSize: '9.5px', color: 'text.disabled' }}>{centerCaption}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
                        {data.map((item, index) => (
                            <LegendRow key={`${item.label}-${index}`} color={item.color} label={item.label} value={item.displayValue ?? item.value} />
                        ))}
                    </Box>
                </Box>
            ) : (
                <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title={emptyTitle} subtitle={emptySubtitle} />
            )}
        </ChartPanel>
    );
}

// Gráfico de barras genérico com 2 séries (contagem/valor por categoria).
function GroupedBarChart({ dataset, xKey, series, loading, height = 240, emptyTitle, emptySubtitle }) {
    const theme = useTheme();
    return (
        <Box sx={{ height, width: '100%' }}>
            {loading ? (
                <Skeleton variant="rounded" width="100%" height={height - 20} />
            ) : dataset && dataset.length > 0 ? (
                <BarChart
                    dataset={dataset}
                    height={height - 20}
                    margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
                    borderRadius={5}
                    hideLegend
                    xAxis={[{
                        scaleType: 'band',
                        dataKey: xKey,
                        categoryGapRatio: 0.4,
                        disableLine: true,
                        disableTicks: true,
                        tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                    }]}
                    yAxis={[{ disableLine: true, disableTicks: true, width: 84, valueFormatter: (v) => formatBrl(v) }]}
                    series={series}
                    sx={{ '& .MuiChartsGrid-line': { stroke: theme.palette.divider } }}
                />
            ) : (
                <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title={emptyTitle} subtitle={emptySubtitle} />
            )}
        </Box>
    );
}

/**
 * Combo chart — DESIGN-SYSTEM.md §5.20 "Combo chart: barras divergentes +
 * linha em escala independente". Barras de entradas (positivas) e saídas
 * (negativas, para render abaixo da linha de base em y=0) empilhadas na
 * mesma coluna via `stack` (stacking `diverging`, padrão de
 * `@mui/x-charts`), sobre um eixo Y esquerdo em reais; linha de saldo
 * acumulado sobre um segundo eixo Y (direito), independente.
 */
function CashFlowComboChart({ rows, loading, theme }) {
    const hasData = rows.length > 0 && rows.some((r) => r.inflows !== 0 || r.outflowsNeg !== 0);
    const minWidth = Math.max(520, rows.length * 78);
    const axisTickSx = { fontSize: 10.5, fill: theme.palette.text.disabled };

    if (loading) {
        return <Skeleton variant="rounded" width="100%" height={300} />;
    }
    if (!hasData) {
        return (
            <EmptyMiniState
                icon={<BarChart3 size={22} strokeWidth={2} />}
                title="Nenhuma movimentação no período"
                subtitle="Sem entradas ou saídas nos meses selecionados"
            />
        );
    }
    return (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Box sx={{ minWidth, height: 300 }}>
                <ChartContainer
                    height={300}
                    margin={{ top: 16, right: 8, bottom: 32, left: 8 }}
                    xAxis={[{
                        id: 'months',
                        data: rows.map((r) => r.label),
                        scaleType: 'band',
                        position: 'bottom',
                        tickLabelStyle: axisTickSx,
                    }]}
                    yAxis={[
                        { id: 'flow', scaleType: 'linear', position: 'left', width: 84, valueFormatter: (v) => formatBrl(v), tickLabelStyle: axisTickSx },
                        { id: 'balance', scaleType: 'linear', position: 'right', width: 88, valueFormatter: (v) => formatBrl(v), tickLabelStyle: axisTickSx },
                    ]}
                    series={[
                        {
                            type: 'bar',
                            id: 'inflows',
                            yAxisId: 'flow',
                            stack: 'flow',
                            stackOffset: 'diverging',
                            data: rows.map((r) => r.inflows),
                            label: 'Entradas',
                            color: theme.palette.primary.main,
                            valueFormatter: (v) => formatBrl(v),
                        },
                        {
                            type: 'bar',
                            id: 'outflows',
                            yAxisId: 'flow',
                            stack: 'flow',
                            stackOffset: 'diverging',
                            data: rows.map((r) => r.outflowsNeg),
                            label: 'Saídas',
                            color: theme.palette.error.main,
                            valueFormatter: (v) => formatBrl(Math.abs(v ?? 0)),
                        },
                        {
                            type: 'line',
                            id: 'balance',
                            yAxisId: 'balance',
                            data: rows.map((r) => r.balance),
                            label: 'Saldo acumulado',
                            color: theme.palette.secondary.main,
                            curve: 'monotoneX',
                            valueFormatter: (v) => formatBrl(v),
                        },
                    ]}
                    sx={{
                        '& .MuiChartsAxis-line': { stroke: theme.palette.divider },
                        '& .MuiChartsAxis-tick': { stroke: theme.palette.divider },
                        '& .MuiChartsGrid-line': { stroke: theme.palette.divider },
                        '& .MuiLineElement-root': { strokeWidth: 2.5 },
                        '& .MuiMarkElement-root': { fill: theme.palette.background.paper, stroke: theme.palette.secondary.main, strokeWidth: 2.5 },
                    }}
                >
                    <ChartsGrid horizontal />
                    <BarPlot borderRadius={4} />
                    <LinePlot />
                    <MarkPlot />
                    <ChartsReferenceLine y={0} axisId="flow" lineStyle={{ stroke: theme.palette.divider, strokeDasharray: '4 4' }} />
                    <ChartsXAxis axisId="months" />
                    <ChartsYAxis axisId="flow" />
                    <ChartsYAxis axisId="balance" />
                    <ChartsTooltip trigger="axis" />
                </ChartContainer>
            </Box>
        </Box>
    );
}

export default function CashFlowReport() {
    const theme = useTheme();
    const { hasAnyPermission, loading: permLoading, userData } = usePermissions();

    const [startDate, setStartDate] = useState(() => dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(() => dayjs().endOf('month'));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const initialFetchDoneRef = useRef(false);

    const canAccess = hasAnyPermission(CASH_FLOW_PERMISSIONS);

    const [dialogType, setDialogType] = useState('warning');

    const showDialog = (msg, type = 'warning') => {
        setDialogMessage(msg);
        setDialogType(type);
        setOpenDialog(true);
    };

    const fetchCashFlow = async ({ useDefaultRange = false } = {}) => {
        if (!canAccess) return;

        if (!useDefaultRange) {
            if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) {
                showDialog('Selecione datas de início e fim válidas.');
                return;
            }
            if (endDate.isBefore(startDate, 'day')) {
                showDialog('A data final não pode ser anterior à data inicial.');
                return;
            }
        }

        setLoading(true);
        try {
            const params = {};
            if (!useDefaultRange) {
                params.start_date = toApiDate(startDate);
                params.end_date = toApiDate(endDate);
            }

            const response = await httpClient.get('/dashboard/cash-flow', { params });

            if (response.status === 200 && response.data && Array.isArray(response.data.period)) {
                setData(response.data);
                return;
            }

            const body = response.data;
            const msg = typeof body === 'string' ? body : body?.message || 'Não foi possível carregar o fluxo de caixa.';
            setData(null);
            showDialog(msg, 'error');
        } catch (e) {
            setData(null);
            showDialog('Erro ao carregar o fluxo de caixa. Tente novamente.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchCashFlowRef = useRef(fetchCashFlow);
    fetchCashFlowRef.current = fetchCashFlow;

    useEffect(() => {
        if (permLoading || !userData || !canAccess) return;
        if (initialFetchDoneRef.current) return;
        initialFetchDoneRef.current = true;
        fetchCashFlow({ useDefaultRange: true });
        // Intencional: carrega uma vez ao montar com o intervalo padrão da API (mês corrente).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permLoading, userData, canAccess]);

    useEffect(() => {
        if (!canAccess) return undefined;
        const onInvalidate = () => {
            fetchCashFlowRef.current({ useDefaultRange: false });
        };
        window.addEventListener(CASH_FLOW_INVALIDATE_EVENT, onInvalidate);
        return () => window.removeEventListener(CASH_FLOW_INVALIDATE_EVENT, onInvalidate);
    }, [canAccess]);

    const handleApply = () => fetchCashFlow({ useDefaultRange: false });

    const handleReset = () => {
        setStartDate(dayjs().startOf('month'));
        setEndDate(dayjs().endOf('month'));
        initialFetchDoneRef.current = true;
        fetchCashFlow({ useDefaultRange: true });
    };

    const isMobileShell = useIsMobileShell();
    // Mobile: a carga inicial é a do próprio efeito acima; trocas de período/filtros repetem o "Aplicar filtros".
    const regenerate = useMobileRegenerate(handleApply, { enabled: isMobileShell && canAccess, skipInitial: true });

    const handleExportCsv = () => {
        if (!data?.period?.length) {
            showDialog('Não há dados para exportar.', 'info');
            return;
        }
        const sep = ';';
        const headers = ['Ano', 'Mês', 'Rótulo', 'Entradas', 'Saídas', 'Líquido mês', 'Saldo início', 'Saldo fim'];
        const rows = data.period.map((p) =>
            [p.year, p.month, monthAxisLabel(p), p.inflows, p.outflows, p.net, p.opening_balance, p.closing_balance].join(sep)
        );
        const bom = '\uFEFF';
        const csv = bom + [headers.join(sep), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fluxo_caixa_${data.start_date}_${data.end_date}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const periodIsEmpty = data?.period?.length > 0 && data.period.every((p) => Number(p.inflows) === 0 && Number(p.outflows) === 0);

    const negativeMonths = useMemo(() => (data?.period || []).filter((p) => Number(p.net) < 0), [data]);
    const positiveMonthsCount = (data?.period?.length || 0) - negativeMonths.length;
    const positivePct = data?.period?.length ? Math.round((positiveMonthsCount / data.period.length) * 100) : 0;

    // Combo chart (§5.20) — barras de entradas/saídas + linha de saldo
    // acumulado, 1:1 com `data.period` (nada agregado/derivado aqui).
    const comboChartRows = useMemo(() => {
        if (!data?.period?.length) return [];
        return data.period.map((row) => ({
            label: monthAxisLabel(row),
            inflows: Number(row.inflows) || 0,
            outflowsNeg: -(Number(row.outflows) || 0),
            balance: Number(row.closing_balance) || 0,
        }));
    }, [data]);

    // "Entradas x Saídas por trimestre" — soma das linhas mensais reais
    // agrupadas por trimestre-calendário (client-side, sem endpoint novo).
    const quarterlyData = useMemo(() => {
        if (!data?.period?.length) return [];
        const map = new Map();
        data.period.forEach((row) => {
            const q = Math.ceil(row.month / 3);
            const key = `${row.year}-${q}`;
            if (!map.has(key)) map.set(key, { key, label: `T${q}/${row.year}`, order: row.year * 10 + q, inflows: 0, outflows: 0 });
            const entry = map.get(key);
            entry.inflows += Number(row.inflows) || 0;
            entry.outflows += Number(row.outflows) || 0;
        });
        return Array.from(map.values()).sort((a, b) => a.order - b.order);
    }, [data]);

    const compositionDonutData = useMemo(() => {
        if (!data) return [];
        return [
            { id: 0, label: 'Entradas', value: Number(data.total_inflows) || 0, color: theme.palette.primary.main },
            { id: 1, label: 'Saídas', value: Number(data.total_outflows) || 0, color: theme.palette.error.main },
        ];
    }, [data, theme]);

    const monthsDonutData = useMemo(() => {
        if (!data?.period?.length) return [];
        return [
            { id: 0, label: 'Meses positivos', value: positiveMonthsCount, color: theme.palette.success.main },
            { id: 1, label: 'Meses negativos', value: negativeMonths.length, color: theme.palette.error.main },
        ];
    }, [data, positiveMonthsCount, negativeMonths, theme]);

    const composedTotal = (Number(data?.total_inflows) || 0) + (Number(data?.total_outflows) || 0);
    const inflowsSharePct = composedTotal > 0 ? Math.round(((Number(data?.total_inflows) || 0) / composedTotal) * 100) : 0;

    const negativeAlertMessage = useMemo(() => {
        if (!negativeMonths.length) return null;
        if (negativeMonths.length === 1) {
            const m = negativeMonths[0];
            return (
                <>
                    <b>{monthAxisLabel(m)}</b> foi o único mês do período com resultado líquido negativo (<b>{formatBrl(m.net)}</b>).
                </>
            );
        }
        const sum = negativeMonths.reduce((acc, m) => acc + Number(m.net), 0);
        const labels = negativeMonths.map((m) => monthAxisLabel(m)).join(', ');
        return (
            <>
                <b>{negativeMonths.length} meses</b> do período tiveram resultado líquido negativo (<b>{labels}</b>), somando <b>{formatBrl(sum)}</b>.
            </>
        );
    }, [negativeMonths]);

    if (permLoading) {
        return (
            <PageScaffold title="Relatório de Fluxo de Caixa" titlePageTarget="Fluxo de caixa" links={[{ title: 'Relatórios', link: '/reports' }]}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            </PageScaffold>
        );
    }

    if (userData && !canAccess) {
        return <Navigate to="/reports" replace />;
    }

    if (isMobileShell) {
        const periodRows = data?.period || [];
        const multiYear = new Set(periodRows.map((row) => row.year)).size > 1;
        const monthShort = (row) => `${capitalize(String(row.month_label || '').slice(0, 3))}${multiYear ? `/${String(row.year).slice(-2)}` : ''}`;
        // Mesmas somas do desktop (valores já calculados acima); só formatação para o KPI.
        const moneyKpi = (raw) => {
            const n = Number(raw) || 0;
            return Math.abs(n) >= 100000 ? formatMoneyKpi(n) : formatBrl(n);
        };
        const segmentsOf = (list) => list.map((item) => ({ value: item.value, color: item.color }));
        const hasFlow = periodRows.some((row) => Number(row.inflows) !== 0 || Number(row.outflows) !== 0);
        const balances = periodRows.map((row) => Number(row.closing_balance) || 0);
        const minBalance = Math.min(0, ...balances);
        const balanceItems = periodRows.map((row, index) => ({ label: monthShort(row), value: balances[index] - minBalance }));
        const noMovement = <KitEmptyNote>Nenhuma movimentação no período</KitEmptyNote>;
        const tabsNode = (
            <Box sx={{ mt: '22px' }}>
                <KitSeg
                    value={activeTab}
                    onChange={setActiveTab}
                    options={[{ value: 0, label: 'Tabela mensal' }, { value: 1, label: 'Gráficos' }]}
                />
                <Box sx={{ mt: '14px' }}>
                    {activeTab === 0 ? (
                        <>
                            <CashFlowNotices negativeMessage={negativeAlertMessage} periodIsEmpty={periodIsEmpty} />
                            {data && <MonthlyCashList data={data} formatBrl={formatBrl} monthLabel={monthAxisLabel} />}
                        </>
                    ) : (
                        <>
                            <ChartCard title="Entradas x Saídas por mês" subtitle="Entradas e saídas de cada mês do período">
                                {hasFlow ? (
                                    <>
                                        <BarsChart
                                            items={periodRows.map((row) => ({ label: monthShort(row), value: Number(row.inflows) || 0 }))}
                                            alt={periodRows.map((row) => ({ value: Number(row.outflows) || 0 }))}
                                            pairWidth={12}
                                        />
                                        <SeriesLegend items={[{ label: 'Entradas', color: theme.palette.primary.main }, { label: 'Saídas', color: theme.palette.secondary.main }]} />
                                    </>
                                ) : noMovement}
                            </ChartCard>

                            <ChartCard title="Saldo acumulado" subtitle="Saldo ao fim de cada mês">
                                {hasFlow ? (
                                    <>
                                        <AreaLine items={balanceItems} />
                                        <Caption>{`Menor saldo ${formatBrl(Math.min(...balances))} · Maior saldo ${formatBrl(Math.max(...balances))}`}</Caption>
                                    </>
                                ) : noMovement}
                            </ChartCard>

                            <ChartCard title="Composição do período" subtitle="Participação de entradas x saídas no total movimentado" bare>
                                <DonutBlock
                                    segments={segmentsOf(compositionDonutData)}
                                    center={`${inflowsSharePct}%`}
                                    legend={compositionDonutData.map((d) => ({ label: `${d.label} · ${formatBrl(d.value)}`, color: d.color }))}
                                    empty="Sem movimentações no período"
                                />
                            </ChartCard>

                            <ChartCard title="Entradas x Saídas por trimestre">
                                {quarterlyData.length > 0 && hasFlow ? (
                                    <>
                                        <BarsChart
                                            items={quarterlyData.map((q) => ({ label: q.label, value: q.inflows }))}
                                            alt={quarterlyData.map((q) => ({ value: q.outflows }))}
                                            pairWidth={12}
                                        />
                                        <SeriesLegend items={[{ label: 'Entradas', color: theme.palette.primary.main }, { label: 'Saídas', color: theme.palette.secondary.main }]} />
                                    </>
                                ) : noMovement}
                            </ChartCard>

                            <ChartCard title="Meses positivos x negativos" bare>
                                <DonutBlock
                                    segments={segmentsOf(monthsDonutData)}
                                    center={`${positivePct}%`}
                                    legend={monthsDonutData.map((d) => ({ label: `${d.label} · ${d.value}`, color: d.color }))}
                                    empty="Sem meses no período"
                                />
                            </ChartCard>
                        </>
                    )}
                </Box>
            </Box>
        );
        return (
            <PageScaffold title="Relatório de Fluxo de Caixa" titlePageTarget="Fluxo de caixa" links={[{ title: 'Relatórios', link: '/reports' }]}>
                <MobileReport
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    onGenerate={regenerate}
                    onClear={handleReset}
                    exportItems={data?.period?.length ? [{ label: 'Excel (CSV)', icon: protoIcon('download'), onClick: handleExportCsv }] : []}
                >
                    <MobileReportBody
                        loading={loading}
                        ready={Boolean(data)}
                        kpis={[
                            { label: 'Total de entradas', icon: 'downleft', grad: true, value: moneyKpi(data?.total_inflows) },
                            { label: 'Total de saídas', icon: 'upright', value: moneyKpi(data?.total_outflows) },
                            { label: 'Resultado líquido', icon: 'wallet', value: moneyKpi(data?.net_period) },
                            { label: 'Saldo atual', icon: 'check', value: moneyKpi(data?.closing_balance) },
                            { label: data ? `Saldo inicial (${dayjs(data.start_date).format('DD/MM')})` : 'Saldo inicial', icon: 'bank', value: moneyKpi(data?.opening_balance) },
                            { label: negativeMonths.length === 1 ? 'Mês negativo' : 'Meses negativos', icon: 'alert', value: String(negativeMonths.length) },
                        ]}
                        sections={[
                            { type: 'node', title: 'Detalhamento', node: tabsNode },
                        ]}
                    />
                </MobileReport>
                <DialogForm
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    onSubmit={() => setOpenDialog(false)}
                    title="Fluxo de caixa"
                    iconType={dialogType}
                    content={dialogMessage}
                    cancelButtonText="Fechar"
                    hideConfirmButton
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold title="Relatório de Fluxo de Caixa" titlePageTarget="Fluxo de caixa" links={[{ title: 'Relatórios', link: '/reports' }]}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                    {/* page-head — ícone + título + descrição + ação primária (Exportar CSV). */}
                    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: (t) => t.palette.gradient.brand }}>
                                <Banknote size={18} strokeWidth={2} />
                            </Box>
                            <Box>
                                <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                    Relatório de Fluxo de Caixa
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                    Entradas, saídas e evolução do saldo mês a mês.
                                </Typography>
                            </Box>
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title="Recarregar (mês corrente, padrão da API)">
                                <span>
                                    <IconButton
                                        onClick={handleReset}
                                        disabled={loading}
                                        sx={{ width: 38, height: 38, borderRadius: '10px', border: '1px solid', borderColor: 'divider' }}
                                    >
                                        <RefreshCw size={16} strokeWidth={2} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Box>

                    {/* filter-bar (sem card) — período + ações (Limpar / Exportar / Gerar). */}
                    <ReportFilterBar
                        start={startDate}
                        end={endDate}
                        onPeriodChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                        onGenerate={handleApply}
                        loading={loading}
                        onClear={handleReset}
                        clearGenerates
                        exportItems={[{ label: 'Fluxo de caixa', description: 'Resumo mensal do período (CSV)', onClick: handleExportCsv, disabled: !data?.period?.length }]}
                    />

                    {/* Chips de contexto — período + totais reais devolvidos pela API. */}
                    {data && (
                        <FilterChips sx={{ mb: { xs: 1.5, sm: 3 } }}>
                            <Chip
                                icon={<Calendar size={13} strokeWidth={2} />}
                                label={`Período: ${dayjs(data.start_date).format('DD/MM/YYYY')} – ${dayjs(data.end_date).format('DD/MM/YYYY')}`}
                                size="small"
                                sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                            />
                            <Chip label={`${data.period.length} mês(es) no período`} size="small" sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }} />
                            {negativeMonths.length > 0 && (
                                <Chip
                                    icon={<AlertTriangle size={13} strokeWidth={2} />}
                                    label={`${negativeMonths.length} mês(es) com resultado negativo`}
                                    size="small"
                                    sx={{ bgcolor: 'warning.bg', color: 'warning.main' }}
                                />
                            )}
                        </FilterChips>
                    )}

                    {loading && !data ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : data ? (
                        <>
                            {/* KPI grid — DESIGN-SYSTEM.md §5.2/§6.1. */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: { xs: 1, md: 2 }, mb: { xs: 1.5, md: 3 } }}>
                                <KpiCard
                                    icon={<Wallet size={17} strokeWidth={2} />}
                                    iconBg="info.bg"
                                    iconFg="primary.main"
                                    value={animBrl(data.opening_balance)}
                                    label={`Saldo inicial (${dayjs(data.start_date).format('DD/MM/YYYY')})`}
                                    loading={loading}
                                />
                                <KpiCard
                                    icon={<TrendingUp size={17} strokeWidth={2} />}
                                    iconBg="success.bg"
                                    iconFg="success.main"
                                    value={animBrl(data.total_inflows)}
                                    label="Total de entradas"
                                    loading={loading}
                                />
                                <KpiCard
                                    icon={<TrendingDown size={17} strokeWidth={2} />}
                                    iconBg="error.bg"
                                    iconFg="error.main"
                                    value={animBrl(data.total_outflows)}
                                    label="Total de saídas"
                                    loading={loading}
                                />
                                <KpiCard
                                    icon={<ArrowLeftRight size={17} strokeWidth={2} />}
                                    iconBg="secondary.bg"
                                    iconFg="secondary.main"
                                    value={animBrl(data.net_period)}
                                    valueColor={Number(data.net_period) >= 0 ? 'success.main' : 'error.main'}
                                    label="Resultado líquido"
                                    loading={loading}
                                />
                                <KpiCard
                                    icon={<CheckCircle2 size={17} strokeWidth={2} />}
                                    iconBg="info.bg"
                                    iconFg="brand2.main"
                                    value={animBrl(data.closing_balance)}
                                    label="Saldo atual"
                                    loading={loading}
                                />
                                <KpiCard
                                    icon={<AlertTriangle size={17} strokeWidth={2} />}
                                    iconBg="warning.bg"
                                    iconFg="warning.main"
                                    value={<AnimatedNumber value={negativeMonths.length} format={String} />}
                                    label={negativeMonths.length === 1 ? 'Mês com resultado negativo' : 'Meses com resultado negativo'}
                                    loading={loading}
                                />
                            </Box>

                            {/* seg-tabs — DESIGN-SYSTEM.md §5.4. */}
                            <Stack direction="row" sx={{ width: 'fit-content', p: '4px', gap: '4px', borderRadius: '11px', bgcolor: 'background.subtle', border: '1px solid', borderColor: 'divider', mb: 2.25, flexWrap: 'wrap' }}>
                                {[
                                    { icon: Table2, label: 'Tabela mensal' },
                                    { icon: BarChart3, label: 'Gráficos' },
                                ].map((tab, index) => {
                                    const Icon = tab.icon;
                                    const selected = activeTab === index;
                                    return (
                                        <Button
                                            key={tab.label}
                                            onClick={() => setActiveTab(index)}
                                            startIcon={<Icon size={14} strokeWidth={2} />}
                                            sx={{
                                                gap: '7px',
                                                padding: '9px 18px',
                                                lineHeight: 'normal',
                                                borderRadius: '8px',
                                                fontSize: '12.5px',
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                color: selected ? 'primary.main' : 'text.secondary',
                                                bgcolor: selected ? 'background.paper' : 'transparent',
                                                boxShadow: selected ? (t) => t.customShadows?.sm : 'none',
                                                '&:hover': { bgcolor: selected ? 'background.paper' : 'background.subtle' },
                                            }}
                                        >
                                            {tab.label}
                                        </Button>
                                    );
                                })}
                            </Stack>

                            {periodIsEmpty && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Nenhuma movimentação no período (entradas e saídas zeradas nos meses exibidos).
                                </Alert>
                            )}

                            {/* ===== TAB: TABELA MENSAL ===== */}
                            {activeTab === 0 && (
                                <Stack spacing={2}>
                                    {negativeAlertMessage && (
                                        <Alert severity="warning" icon={<AlertTriangle size={18} strokeWidth={2} />}>
                                            {negativeAlertMessage}
                                        </Alert>
                                    )}

                                    <Alert severity="info">
                                        As <strong>saídas</strong> consideram apenas despesas com status <strong>Prestado Contas</strong>, posicionadas pela{' '}
                                        <strong>data de pagamento</strong> (ou data do lançamento quando aplicável). Despesas <strong>Pendente</strong> não entram
                                        nas saídas consolidadas. O <strong>saldo</strong> é uma posição acumulada a partir das transações cadastradas e não
                                        necessariamente corresponde ao extrato bancário ou ao caixa registradora.
                                    </Alert>

                                    <Card>
                                        <CardContent sx={{ p: 2.25 }}>
                                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mb: 2 }}>
                                                Valores consolidados por mês. A linha <b>Saldo inicial</b> representa o saldo em caixa no início do período
                                                filtrado; a linha <b>Total do período</b> soma entradas e saídas de todos os meses.
                                            </Typography>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            {['Mês', 'Entradas', 'Saídas', 'Líquido', 'Saldo'].map((h, i) => (
                                                                <TableCell
                                                                    key={h}
                                                                    align={i === 0 ? 'left' : 'right'}
                                                                    sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.disabled', borderBottom: '1px solid', borderColor: 'divider' }}
                                                                >
                                                                    {h}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell colSpan={4} sx={{ fontStyle: 'italic', color: 'text.disabled', fontSize: '13px', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                                Saldo inicial ({dayjs(data.start_date).format('DD/MM/YYYY')})
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '13px', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                                {formatBrl(data.opening_balance)}
                                                            </TableCell>
                                                        </TableRow>
                                                        {data.period.map((row) => (
                                                            <TableRow key={`${row.year}-${row.month}`} hover>
                                                                <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '13px', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                                    {monthAxisLabel(row)}
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ fontSize: '13px', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                                    {formatBrl(row.inflows)}
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ fontSize: '13px', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                                    {formatBrl(row.outflows)}
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                                                    <Pill label={`${Number(row.net) >= 0 ? '+' : ''}${formatBrl(row.net)}`} tone={Number(row.net) >= 0 ? 'green' : 'red'} />
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '13px', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                                    {formatBrl(row.closing_balance)}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 800, color: 'text.primary', fontSize: '13px', borderTop: '2px solid', borderColor: 'divider', borderBottom: 'none', bgcolor: 'background.subtle' }}>
                                                                Total do período
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '13px', borderTop: '2px solid', borderColor: 'divider', borderBottom: 'none', bgcolor: 'background.subtle' }}>
                                                                {formatBrl(data.total_inflows)}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '13px', borderTop: '2px solid', borderColor: 'divider', borderBottom: 'none', bgcolor: 'background.subtle' }}>
                                                                {formatBrl(data.total_outflows)}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ borderTop: '2px solid', borderColor: 'divider', borderBottom: 'none', bgcolor: 'background.subtle' }}>
                                                                <Pill label={`${Number(data.net_period) >= 0 ? '+' : ''}${formatBrl(data.net_period)}`} tone={Number(data.net_period) >= 0 ? 'green' : 'red'} />
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '13px', borderTop: '2px solid', borderColor: 'divider', borderBottom: 'none', bgcolor: 'background.subtle' }}>
                                                                {formatBrl(data.closing_balance)}
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </CardContent>
                                    </Card>
                                </Stack>
                            )}

                            {/* ===== TAB: GRÁFICOS ===== */}
                            {activeTab === 1 && (
                                <Stack spacing={2}>
                                    <Card>
                                        <CardContent sx={{ p: 2.25 }}>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>
                                                    Entradas x Saídas por mês, com saldo acumulado
                                                </Typography>
                                                <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>
                                                    Barras: entradas (acima) e saídas (abaixo) — escala à esquerda · Linha: saldo acumulado — escala independente à direita
                                                </Typography>
                                            </Box>
                                            <CashFlowComboChart rows={comboChartRows} loading={loading} theme={theme} />
                                            <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ mt: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: 'primary.main' }} />
                                                    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>Entradas</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: 'error.main' }} />
                                                    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>Saídas</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: 'secondary.main' }} />
                                                    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>Saldo acumulado</Typography>
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2, alignItems: 'stretch' }}>
                                        <DonutPanel
                                            title="Composição do período"
                                            subtitle="Participação de entradas x saídas no total movimentado"
                                            data={compositionDonutData.map((d) => ({ ...d, displayValue: formatBrl(d.value) }))}
                                            centerValue={`${inflowsSharePct}%`}
                                            centerCaption="entradas"
                                            loading={loading}
                                            emptyTitle="Nenhum dado disponível"
                                            emptySubtitle="Sem movimentações no período"
                                        />

                                        <ChartPanel title="Entradas x Saídas por trimestre" loading={loading}>
                                            <GroupedBarChart
                                                dataset={quarterlyData}
                                                xKey="label"
                                                loading={loading}
                                                emptyTitle="Nenhum dado disponível"
                                                emptySubtitle="Sem movimentações no período"
                                                series={[
                                                    { dataKey: 'inflows', label: 'Entradas', color: theme.palette.primary.main, valueFormatter: (v) => formatBrl(v) },
                                                    { dataKey: 'outflows', label: 'Saídas', color: theme.palette.error.main, valueFormatter: (v) => formatBrl(v) },
                                                ]}
                                            />
                                            <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ mt: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: 'primary.main' }} />
                                                    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>Entradas</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: 'error.main' }} />
                                                    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>Saídas</Typography>
                                                </Box>
                                            </Stack>
                                        </ChartPanel>

                                        <DonutPanel
                                            title="Meses positivos x negativos"
                                            data={monthsDonutData}
                                            centerValue={`${positivePct}%`}
                                            centerCaption="positivos"
                                            loading={loading}
                                            emptyTitle="Nenhum dado disponível"
                                            emptySubtitle="Sem meses no período"
                                        />
                                    </Box>
                                </Stack>
                            )}
                        </>
                    ) : (
                        <Card>
                            <CardContent sx={{ py: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
                                <Box sx={{ width: 72, height: 72, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                                    <Inbox size={30} strokeWidth={2} />
                                </Box>
                                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.secondary' }}>Nenhum dado disponível</Typography>
                                <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', maxWidth: 380 }}>
                                    Selecione um período e clique em "Aplicar filtros" para visualizar o fluxo de caixa.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </LocalizationProvider>

            <DialogForm
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onSubmit={() => setOpenDialog(false)}
                title="Fluxo de caixa"
                iconType={dialogType}
                content={dialogMessage}
                cancelButtonText="Fechar"
                hideConfirmButton
            />
        </PageScaffold>
    );
}
